import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { canSeeModule, hasElevatedModuleAccess, isStrictAdmin } from "@/lib/rbac";
import Login from "@/pages/Login";
import Employees from "@/pages/Employees";
import Dashboard from "@/pages/Dashboard";
import Departments from "@/pages/Departments";
import Designations from "@/pages/Designations";
import Company from "@/pages/Company";
import Companies from "@/pages/Companies";
import LeaveTypes from "@/pages/LeaveTypes";
import Leaves from "@/pages/Leaves";
import LeaveReports from "@/pages/LeaveReports";
import Payroll from "@/pages/Payroll";
import Attendance from "@/pages/Attendance";
import Holidays from "@/pages/Holidays";
import Letters from "@/pages/Letters";
import LetterTemplates from "@/pages/LetterTemplates";
import IdCardTemplates from "@/pages/IdCardTemplates";
import Users from "@/pages/Users";
import Roles from "@/pages/Roles";
import Approvals from "@/pages/Approvals";
import Assets from "@/pages/Assets";
import SalaryRevisions from "@/pages/SalaryRevisions";
import Locations from "@/pages/Locations";
import EmployeeIdPrefixes from "@/pages/EmployeeIdPrefixes";
import EmployeeProfile from "@/pages/EmployeeProfile";
import Candidates from "@/pages/Candidates";
import CandidateProfile from "@/pages/CandidateProfile";
import Placeholder from "@/pages/Placeholder";
import DocumentApprovalsSettings from "@/pages/DocumentApprovalsSettings";
import LeaveCalendar from "@/pages/LeaveCalendar";
import PayrollWizard from "@/pages/PayrollWizard";
import StatutoryReports from "@/pages/StatutoryReports";
import AttendanceRegularizations from "@/pages/AttendanceRegularizations";
import { EmployeeReports, AttendanceReports, PayrollReports, SeparationReports } from "@/pages/Reports";
import BankDetailsReport from "@/pages/BankDetailsReport";
import HeadcountReport from "@/pages/HeadcountReport";
import MyProfile from "@/pages/MyProfile";
import MyLeaves from "@/pages/MyLeaves";
import MyAttendance from "@/pages/MyAttendance";
import MyPayslips from "@/pages/MyPayslips";
import MyLetters from "@/pages/MyLetters";
import MyRegularization from "@/pages/MyRegularization";
import TeamLeaveCalendar from "@/pages/TeamLeaveCalendar";
import MyTax from "@/pages/MyTax";
import TaxAdmin from "@/pages/TaxAdmin";
import ChangePassword from "@/pages/ChangePassword";

function AdminRoute({ children }) {
  // Custom roles (Payroll Admin, Holiday Viewer, …) may open staff module pages.
  // Pure Employee ESS stays on /dashboard self-service.
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasElevatedModuleAccess(user)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminOnlyRoute({ children }) {
  // Strict admin (legacy role or role_admin) — User Management / Companies / Roles
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStrictAdmin(user)) return <Navigate to="/dashboard" replace />;
  return children;
}

function DocumentApprovalsRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStrictAdmin(user) && !canSeeModule(user, "document_approvals")) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin only */}
            <Route
              path="/employees"
              element={
                <AdminRoute>
                  <Employees />
                </AdminRoute>
              }
            />
            <Route
              path="/candidates"
              element={
                <AdminRoute>
                  <Candidates />
                </AdminRoute>
              }
            />
            <Route
              path="/candidates/:id"
              element={
                <AdminRoute>
                  <CandidateProfile />
                </AdminRoute>
              }
            />
            <Route
              path="/employees/:id"
              element={
                <AdminRoute>
                  <EmployeeProfile />
                </AdminRoute>
              }
            />
            <Route
              path="/locations"
              element={
                <AdminRoute>
                  <Locations />
                </AdminRoute>
              }
            />
            <Route
              path="/organization/employee-ids"
              element={
                <AdminRoute>
                  <EmployeeIdPrefixes />
                </AdminRoute>
              }
            />
            <Route path="/leave-calendar" element={<AdminRoute><LeaveCalendar /></AdminRoute>} />
            <Route path="/attendance/regularizations" element={<AdminRoute><AttendanceRegularizations /></AdminRoute>} />
            <Route path="/payroll/statutory" element={<AdminRoute><StatutoryReports /></AdminRoute>} />
            <Route path="/payroll/wizard" element={<AdminRoute><PayrollWizard /></AdminRoute>} />
            <Route path="/salary-revisions" element={<AdminRoute><SalaryRevisions /></AdminRoute>} />
            <Route path="/performance/reviews" element={<AdminOnlyRoute><Placeholder title="Performance Reviews" breadcrumbs={["Home","Performance"]} phase="13" /></AdminOnlyRoute>} />
            <Route path="/performance/goals" element={<AdminOnlyRoute><Placeholder title="Goals / KPI Setup" breadcrumbs={["Home","Performance"]} phase="13" /></AdminOnlyRoute>} />
            <Route path="/performance/cycles" element={<AdminOnlyRoute><Placeholder title="Appraisal Cycles" breadcrumbs={["Home","Performance"]} phase="13" /></AdminOnlyRoute>} />
            <Route path="/reports/employees" element={<AdminRoute><EmployeeReports /></AdminRoute>} />
            <Route path="/reports/bank-details" element={<AdminRoute><BankDetailsReport /></AdminRoute>} />
            <Route path="/reports/headcount" element={<AdminRoute><HeadcountReport /></AdminRoute>} />
            <Route path="/reports/separations" element={<AdminRoute><SeparationReports /></AdminRoute>} />
            <Route path="/reports/attendance" element={<AdminRoute><AttendanceReports /></AdminRoute>} />
            <Route path="/reports/payroll" element={<AdminRoute><PayrollReports /></AdminRoute>} />
            <Route path="/settings/system" element={<AdminOnlyRoute><Placeholder title="System Settings" breadcrumbs={["Home","Settings"]} phase="future" /></AdminOnlyRoute>} />
            <Route
              path="/settings/document-approvals"
              element={
                <DocumentApprovalsRoute>
                  <DocumentApprovalsSettings />
                </DocumentApprovalsRoute>
              }
            />
            <Route
              path="/finance/po"
              element={
                <AdminRoute>
                  <Placeholder title="Purchase Orders" breadcrumbs={["Home", "Finance"]} />
                </AdminRoute>
              }
            />
            <Route
              path="/finance/invoices"
              element={
                <AdminRoute>
                  <Placeholder title="Invoices" breadcrumbs={["Home", "Finance"]} />
                </AdminRoute>
              }
            />
            <Route
              path="/leaves"
              element={
                <AdminRoute>
                  <Leaves />
                </AdminRoute>
              }
            />
            <Route
              path="/leave-reports"
              element={
                <AdminRoute>
                  <LeaveReports />
                </AdminRoute>
              }
            />
            <Route
              path="/payroll"
              element={
                <AdminRoute>
                  <Payroll />
                </AdminRoute>
              }
            />
            <Route
              path="/attendance"
              element={
                <AdminRoute>
                  <Attendance />
                </AdminRoute>
              }
            />
            <Route
              path="/letters"
              element={
                <AdminRoute>
                  <Letters />
                </AdminRoute>
              }
            />
            <Route
              path="/holidays"
              element={
                <ProtectedRoute>
                  <Holidays />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets"
              element={
                <AdminRoute>
                  <Assets />
                </AdminRoute>
              }
            />
            <Route
              path="/departments"
              element={
                <AdminRoute>
                  <Departments />
                </AdminRoute>
              }
            />
            <Route
              path="/designations"
              element={
                <AdminRoute>
                  <Designations />
                </AdminRoute>
              }
            />
            <Route
              path="/leave-types"
              element={
                <AdminRoute>
                  <LeaveTypes />
                </AdminRoute>
              }
            />
            <Route
              path="/companies"
              element={
                <AdminOnlyRoute>
                  <Companies />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/companies/:companyId"
              element={
                <AdminOnlyRoute>
                  <Company />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/company"
              element={<Navigate to="/companies/company" replace />}
            />
            <Route
              path="/users"
              element={
                <AdminOnlyRoute>
                  <Users />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/roles"
              element={
                <AdminOnlyRoute>
                  <Roles />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/approvals"
              element={
                <ProtectedRoute>
                  <Approvals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/letter-templates"
              element={
                <AdminOnlyRoute>
                  <LetterTemplates />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/id-card-templates"
              element={
                <AdminOnlyRoute>
                  <IdCardTemplates />
                </AdminOnlyRoute>
              }
            />

            {/* Employee self-service */}
            <Route
              path="/me/profile"
              element={
                <ProtectedRoute>
                  <MyProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/me/leaves"
              element={
                <ProtectedRoute>
                  <MyLeaves />
                </ProtectedRoute>
              }
            />
            <Route
              path="/me/attendance"
              element={
                <ProtectedRoute>
                  <MyAttendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/me/payslips"
              element={
                <ProtectedRoute>
                  <MyPayslips />
                </ProtectedRoute>
              }
            />
            <Route
              path="/me/letters"
              element={
                <ProtectedRoute>
                  <MyLetters />
                </ProtectedRoute>
              }
            />
            <Route
              path="/me/regularization"
              element={
                <ProtectedRoute>
                  <MyRegularization />
                </ProtectedRoute>
              }
            />
            <Route
              path="/me/team-leave"
              element={
                <ProtectedRoute>
                  <TeamLeaveCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/me/tax"
              element={
                <ProtectedRoute>
                  <MyTax />
                </ProtectedRoute>
              }
            />
            <Route path="/me/assets" element={<Navigate to="/me/profile" replace />} />
            <Route path="/me/timeline" element={<Navigate to="/me/profile" replace />} />
            <Route
              path="/payroll/tax"
              element={
                <AdminRoute>
                  <TaxAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/account/password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
